import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion"
import { useTranslation } from "react-i18next"
import { PageContainer } from "@/components/core/PageContainer"
import { ReportIssueForm } from "../components/blocks/Support/ReportIssueForm"
import { DeleteForm } from "../components/blocks/Support/DeleteForm"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { PhoneIcon, ChevronRight } from "lucide-react"
import { CONTACT } from "@/consts/contact.consts"
import { useState, useEffect } from "react"
import { Title } from "@/components/core/Title"
import { useLocation } from "react-router-dom"
import { SUPPORT_ACCORDION_SECTIONS } from "@/consts/support.consts"
import { useDisableLocationSelection } from "@/hooks/useDisableLocationSelection"

interface SupportPageState {
  initialFormState?: {
    transactionId?: string;
    description?: string;
  };
  expandAccordion?: SUPPORT_ACCORDION_SECTIONS;
}
interface SupportProps {
  className?: string
}

export function Support({ className }: SupportProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const state = location.state as SupportPageState | undefined
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(undefined)
  
  // Disable location selection on this page
  useDisableLocationSelection(true)

  useEffect(() => {
    if (state?.expandAccordion) {
      setActiveAccordion(state.expandAccordion)
    }
  }, [state?.expandAccordion])

  const handleAccordionChange = (value: string) => {
    setActiveAccordion(value === activeAccordion ? undefined : value)
  }

  return (
    <PageContainer className={`max-w-3xl ${className}`} scrollable>
      <Title>{t("navigation.support")}</Title>

      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">
              {t('support.help.description')}
            </p>
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="accent"
                  className="w-full p-4 h-auto"
                  onClick={() => window.location.href = `tel:${CONTACT.SUPPORT_PHONE}`}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2 pr-2 overflow-hidden">
                      <PhoneIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate text-sm block">
                        {t('support.help.callSupport', { phone: CONTACT.SUPPORT_PHONE })}
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('support.help.callSupportTooltip', { phone: CONTACT.SUPPORT_PHONE })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={activeAccordion}
          onValueChange={handleAccordionChange}
        >
          <AccordionItem value={SUPPORT_ACCORDION_SECTIONS.REPORT_ISSUE}>
            <AccordionTrigger aria-label={t("support.reportIssue")}>
              {t("support.reportIssue")}
            </AccordionTrigger>
            <AccordionContent>
              <ReportIssueForm initialState={state?.initialFormState} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value={SUPPORT_ACCORDION_SECTIONS.DELETE_REQUEST}>
            <AccordionTrigger aria-label={t("support.deleteRequest")}>
              {t("support.deleteRequest")}
            </AccordionTrigger>
            <AccordionContent>
              <DeleteForm />
            </AccordionContent>
          </AccordionItem>

          {/* <AccordionItem value="resources">
            <AccordionTrigger aria-label={t("support.resources.title")}>
              {t("support.resources.title")}
            </AccordionTrigger>
            <AccordionContent>
              <ResourcesSection />
            </AccordionContent>
          </AccordionItem> */}
        </Accordion>
      </div>
    </PageContainer>
  );
} 